/*!
 * Kinnder Wishlist — lightweight, app-free wishlist for Shopify (OS 2.0)
 * - Saves to the shopper's browser (no login needed)
 * - Heart buttons on product cards + PDP, live header count
 * - Wishlist page with quick add / personalise links
 * - Shareable wishlist links (great for gifting: "send to Nana")
 * - Sends "added product to wishlist" to Omnisend for identified shoppers,
 *   and a "kinnder_wishlist_added" Shopify customer event for every shopper
 */
(function () {
  'use strict';
 
  var KEY = 'kinnder_wishlist_v1';
  var MAX_ITEMS = 100;
  var memory = []; // fallback if localStorage is blocked (private mode etc.)
  var cfg = window.KinnderWishlistConfig || {};
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var pageUrl = cfg.pageUrl || root + 'pages/wishlist';
  var personaliseTag = (cfg.personaliseTag || 'personalisable').toLowerCase();
 
  /* ---------- storage ---------- */
  function read() {
    try {
      var raw = window.localStorage.getItem(KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      return memory.slice();
    }
  }
 
  function write(items) {
    memory = items.slice(0, MAX_ITEMS);
    try { window.localStorage.setItem(KEY, JSON.stringify(memory)); } catch (e) { /* ignore */ }
    render();
    notify();
  }
 
  function has(handle) {
    return read().some(function (i) { return i.handle === handle; });
  }
 
  function add(item) {
    if (!item || !item.handle || has(item.handle)) return false;
    var items = read();
    items.unshift({ handle: item.handle, id: item.id || null, addedAt: Date.now() });
    write(items);
    return true;
  }
 
  function remove(handle) {
    write(read().filter(function (i) { return i.handle !== handle; }));
  }
 
  function toggle(item) {
    if (has(item.handle)) { remove(item.handle); return false; }
    add(item);
    return true;
  }
 
  /* ---------- UI sync ---------- */
  // Idempotent: only writes to the DOM when a value actually changes
  // (so the MutationObserver below can't loop).
  function setAttr(el, name, val) { if (el.getAttribute(name) !== val) el.setAttribute(name, val); }
  function setText(el, val) { if (el.textContent !== val) el.textContent = val; }
 
  function render() {
    var handles = read().map(function (i) { return i.handle; });
    var count = String(handles.length);
 
    document.querySelectorAll('[data-kw-toggle]').forEach(function (btn) {
      var on = handles.indexOf(btn.getAttribute('data-product-handle')) > -1;
      var title = btn.getAttribute('data-product-title') || 'this product';
      setAttr(btn, 'aria-pressed', on ? 'true' : 'false');
      if (btn.classList.contains('is-active') !== on) btn.classList.toggle('is-active', on);
      setAttr(btn, 'aria-label', on ? 'Remove ' + title + ' from wishlist' : 'Save ' + title + ' to wishlist');
      var label = btn.querySelector('[data-kw-label]');
      if (label) {
        setText(label, on
          ? (btn.getAttribute('data-label-on') || 'Saved to wishlist')
          : (btn.getAttribute('data-label-off') || 'Save to wishlist'));
      }
    });
 
    document.querySelectorAll('[data-kw-count]').forEach(function (el) {
      setText(el, count);
      var hide = handles.length === 0;
      if (el.hidden !== hide) el.hidden = hide;
    });
  }
 
  function notify() {
    var items = read();
    document.dispatchEvent(new CustomEvent('kinnder:wishlist:change', { detail: { items: items, count: items.length } }));
  }
 
  /* ---------- toast ---------- */
  var toastEl, toastTimer;
  function toast(html) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'kw-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML = html;
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.classList.remove('is-visible'); }, 3200);
  }
 
  /* ---------- tracking: Omnisend (who) + Shopify Customer Events (totals) ---------- */
  function trackWishlist(product) {
    // Omnisend custom event - recorded on the contact's profile when Omnisend knows who they are
    // (clicked an Omnisend email, submitted an Omnisend form, or previously identified).
    try {
      window.omnisend = window.omnisend || [];
      window.omnisend.push(['track', 'added product to wishlist', {
        origin: 'api',
        properties: {
          productID: product.id,
          productHandle: product.handle,
          productTitle: product.title,
          productURL: product.url,
          productImageURL: product.image,
          productPrice: product.price,
          wishlistCount: product.count
        }
      }]);
    } catch (e) { /* ignore */ }
    // Shopify Customer Events - fires for every shopper (anonymous too), usable by a custom pixel / GA4
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish('kinnder_wishlist_added', product);
      }
    } catch (e) { /* ignore */ }
  }
 
  /* ---------- click handling (capture phase so it wins inside product-card links) ---------- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-kw-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
 
    var item = {
      handle: btn.getAttribute('data-product-handle'),
      id: btn.getAttribute('data-product-id')
    };
    var added = toggle(item);
 
    btn.classList.remove('kw-pop');
    void btn.offsetWidth; // restart animation
    if (added) btn.classList.add('kw-pop');
 
    if (added) {
      toast('Saved to your wishlist ♡ <a href="' + pageUrl + '">View wishlist</a>');
      trackWishlist({
        id: item.id,
        handle: item.handle,
        title: btn.getAttribute('data-product-title'),
        url: btn.getAttribute('data-product-url'),
        image: btn.getAttribute('data-product-image'),
        price: btn.getAttribute('data-product-price'),
        count: read().length
      });
    } else {
      toast('Removed from your wishlist');
    }
  }, true);
 
  // keep multiple tabs in sync
  window.addEventListener('storage', function (e) { if (e.key === KEY) { render(); notify(); } });
 
  /* ---------- wishlist page ---------- */
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
 
  function money(cents) {
    var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || cfg.currency || 'AUD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-AU', { style: 'currency', currency: cur, currencyDisplay: 'narrowSymbol' }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }
 
  function img(url, w) {
    if (!url) return '';
    if (url.indexOf('//') === 0) url = 'https:' + url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }
 
  function fetchProduct(handle) {
    return fetch(root + 'products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }
 
  function isPersonalisable(p) {
    return (p.tags || []).some(function (t) { return String(t).toLowerCase() === personaliseTag; });
  }
 
  function cardHTML(p, shared) {
    var url = p.url || (root + 'products/' + p.handle);
    var onSale = p.compare_at_price && p.compare_at_price > p.price;
    var price = (p.price_varies ? 'From ' : '') + money(p.price);
    var action;
 
    if (!p.available) {
      action = '<a class="kw-card__btn kw-card__btn--ghost" href="' + esc(url) + '">Sold out – view</a>';
    } else if (isPersonalisable(p)) {
      action = '<a class="kw-card__btn" href="' + esc(url) + '">Personalise it</a>';
    } else if (p.variants && p.variants.length === 1) {
      // Real Shopify form = guaranteed fallback; JS upgrades it to an in-page add.
      action = '<form class="kw-card__form" method="post" action="' + root + 'cart/add" accept-charset="UTF-8">' +
        '<input type="hidden" name="form_type" value="product">' +
        '<input type="hidden" name="id" value="' + p.variants[0].id + '">' +
        '<input type="hidden" name="quantity" value="1">' +
        '<input type="hidden" name="return_to" value="' + root + 'cart">' +
        '<button type="submit" class="kw-card__btn" data-kw-add="' + p.variants[0].id + '">Add to cart</button>' +
      '</form>';
    } else {
      action = '<a class="kw-card__btn" href="' + esc(url) + '">Choose options</a>';
    }
 
    return '' +
      '<li class="kw-card" data-kw-card="' + esc(p.handle) + '">' +
        '<a class="kw-card__media" href="' + esc(url) + '">' +
          (p.featured_image ? '<img src="' + esc(img(p.featured_image, 600)) + '" alt="' + esc(p.title) + '" loading="lazy" width="600" height="600">' : '') +
          (onSale ? '<span class="kw-card__badge">Sale</span>' : '') +
        '</a>' +
        (shared ? '' : '<button type="button" class="kw-card__remove" data-kw-remove="' + esc(p.handle) + '" aria-label="Remove ' + esc(p.title) + ' from wishlist">×</button>') +
        '<div class="kw-card__body">' +
          '<a class="kw-card__title" href="' + esc(url) + '">' + esc(p.title) + '</a>' +
          '<p class="kw-card__price">' + (onSale ? '<s>' + money(p.compare_at_price) + '</s> ' : '') + esc(price) + '</p>' +
          action +
        '</div>' +
      '</li>';
  }
 
  /* ---------- add to cart from the wishlist page ---------- */
  function refreshThemeCart(count) {
    // Reformation cart badge
    document.querySelectorAll('.thb-item-count').forEach(function (el) { el.textContent = count; });
    // Themes that expose a cart drawer / notification element (Dawn-style)
    ['cart-drawer', 'cart-notification'].forEach(function (tag) {
      var el = document.querySelector(tag);
      if (el && el.classList && el.classList.contains('is-empty') && count > 0) el.classList.remove('is-empty');
    });
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
  }
 
  function addToCart(btn) {
    var id = Number(btn.getAttribute('data-kw-add'));
    btn.disabled = true;
    btn.textContent = 'Adding…';
    fetch(root + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (data) {
        if (!r.ok) {
          var e = new Error(data.description || data.message || 'Could not add to cart');
          // 422 = Shopify refused (e.g. sold out) -> show the reason. Anything else -> use the normal form.
          e.fallback = r.status !== 422;
          throw e;
        }
        return fetch(root + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(function (c) { return c.json(); });
      });
    }).then(function (cart) {
      refreshThemeCart(cart.item_count);
      btn.textContent = 'Added ✓';
      setTimeout(function () { btn.disabled = false; btn.textContent = 'Add to cart'; }, 2500);
      toast('Added to cart ♡ <a href="' + root + 'cart">View cart</a>');
      document.dispatchEvent(new CustomEvent('kinnder:wishlist:added-to-cart', { detail: { variantId: id, cart: cart } }));
    }).catch(function (err) {
      btn.disabled = false;
      btn.textContent = 'Add to cart';
      var form = btn.closest('form');
      if (err && (err.fallback || err.name === 'TypeError') && form) { HTMLFormElement.prototype.submit.call(form); return; }
      toast(esc(err && err.message ? err.message : 'Hmm, that didn’t work – please try again'));
      if (window.console) console.warn('[Kinnder wishlist] add to cart failed:', err);
    });
  }
 
  function initPage() {
    var page = document.querySelector('[data-kw-page]');
    if (!page) return;
 
    var grid = page.querySelector('[data-kw-grid]');
    var empty = page.querySelector('[data-kw-empty]');
    var toolbar = page.querySelector('[data-kw-toolbar]');
    var sharedBanner = page.querySelector('[data-kw-shared]');
    var params = new URLSearchParams(window.location.search);
    var sharedParam = params.get('share');
    var shared = !!sharedParam;
 
    function handlesToShow() {
      if (shared) {
        return sharedParam.split(',').map(function (h) { return h.trim(); })
          .filter(function (h) { return h && h.length < 256; }).slice(0, MAX_ITEMS);
      }
      return read().map(function (i) { return i.handle; });
    }
 
    function draw() {
      var handles = handlesToShow();
      if (sharedBanner) sharedBanner.hidden = !shared;
      if (!handles.length) {
        grid.innerHTML = '';
        empty.hidden = false;
        if (toolbar) toolbar.hidden = true;
        return;
      }
      empty.hidden = true;
      grid.setAttribute('aria-busy', 'true');
      grid.innerHTML = handles.map(function () { return '<li class="kw-card kw-card--skeleton"></li>'; }).join('');
 
      Promise.all(handles.map(fetchProduct)).then(function (products) {
        var found = products.filter(Boolean);
        // tidy up products that no longer exist
        if (!shared && found.length !== handles.length) {
          var live = found.map(function (p) { return p.handle; });
          var clean = read().filter(function (i) { return live.indexOf(i.handle) > -1; });
          memory = clean;
          try { window.localStorage.setItem(KEY, JSON.stringify(clean)); } catch (e) { /* ignore */ }
        }
        grid.innerHTML = found.map(function (p) { return cardHTML(p, shared); }).join('');
        grid.removeAttribute('aria-busy');
        empty.hidden = found.length > 0;
        if (toolbar) toolbar.hidden = found.length === 0;
        render();
      });
    }
 
    page.addEventListener('click', function (e) {
      var rm = e.target.closest('[data-kw-remove]');
      if (rm) {
        var card = rm.closest('[data-kw-card]');
        remove(rm.getAttribute('data-kw-remove'));
        if (card) card.remove();
        if (!grid.children.length) { empty.hidden = false; if (toolbar) toolbar.hidden = true; }
        return;
      }
 
      var addBtn = e.target.closest('[data-kw-add]');
      if (addBtn) {
        e.preventDefault();
        if (addBtn.disabled) return;
        addToCart(addBtn);
        return;
      }
 
      if (e.target.closest('[data-kw-share]')) {
        var handles = read().map(function (i) { return i.handle; });
        var link = window.location.origin + pageUrl + (pageUrl.indexOf('?') > -1 ? '&' : '?') + 'share=' + handles.map(encodeURIComponent).join(',');
        var text = page.getAttribute('data-share-text') || 'Here’s my Kinnder wishlist ♡';
        if (navigator.share) {
          navigator.share({ title: 'My Kinnder wishlist', text: text, url: link }).catch(function () {});
        } else if (navigator.clipboard) {
          navigator.clipboard.writeText(link).then(function () { toast('Link copied – send it to Nana ♡'); });
        } else {
          var input = page.querySelector('[data-kw-share-fallback]');
          if (input) { input.hidden = false; input.value = link; input.select(); }
        }
        return;
      }
 
      if (e.target.closest('[data-kw-save-shared]')) {
        handlesToShow().forEach(function (h) { add({ handle: h }); });
        toast('Saved to your wishlist ♡');
        window.history.replaceState({}, '', pageUrl);
        shared = false;
        sharedParam = null;
        draw();
      }
    });
 
    window.KinnderWishlist.redrawPage = draw;
    draw();
  }
 
  /* ---------- public API + boot ---------- */
  window.KinnderWishlist = { get: read, has: has, add: add, remove: remove, toggle: toggle, refresh: render };
 
  function boot() { render(); initPage(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
 
  // re-sync hearts when themes inject new product cards (infinite scroll, filters, quick view)
  ['shopify:section:load', 'facet:updated', 'collection:updated'].forEach(function (ev) {
    document.addEventListener(ev, render);
  });
  if ('MutationObserver' in window) {
    var pending;
    new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(render, 150);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();